import ReactECharts from "echarts-for-react";
import { useMemo } from "react";
import { Battery, BatteryCharging, BatteryLow, Layers } from "lucide-react";
import { useTopology } from "@/store/site";
import { useUiPref } from "@/store/uiPrefs";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { ChartSkeleton } from "@/components/ui/ChartSkeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { HelpHint } from "@/components/ui/HelpHint";
import { baseOption, barItemStyle, chartTokens, readVar, tooltipBase, tooltipHtml } from "@/lib/chartTheme";
import { cyclesFromThroughput, formatKWh } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { useThemeVersion } from "@/hooks/useThemeVersion";
import { useNow } from "@/hooks/useNow";
import type { BessEnergySample, EnergyGran } from "@/data/adapter";
import {
  BAR_WIDTH_BY_GRAN,
  type BarDatum,
  GranChips,
  Kpi,
  NOTE_GRANS,
  PARTIAL_OPACITY,
  ScopeChips,
  barDatumValue,
  bucketAxisLabel,
  bucketTooltipLabel,
  partialBucketTs,
  useBessEnergyBuckets,
  weekendMarkAreaData,
} from "./bessEnergyBuckets";

/**
 * Energy throughput browser for the BESS page.
 *
 * Sources the AE2-derived `EP_<gran>` (charged) and `EN_<gran>`
 * (discharged) per-bucket kWh timeseries from each BESS device, then
 * lets the operator slice the result two ways:
 *
 *   1. **Granularity**: 15 min · 1 hour · 1 day · 1 week · 1 month · 1 year.
 *      Window auto-snaps to a sensible default per granularity so the
 *      bars never get unreadably packed or sparse.
 *   2. **Scope**: Fleet aggregate, or any subset of BESS units selected
 *      with the chip row.
 *
 * Bars are drawn diverging from zero: **charge above** (blue),
 * **discharge below** (amber), giving the operator a one-glance
 * net-flow read per bucket. The KPI strip beneath totals across the
 * visible window.
 *
 * Sign convention is canonical (positive = energy into the battery /
 * out of the battery, never confused with grid direction).
 *
 * The query, scope aggregation, and capacity-cap sanity filter live in
 * `bessEnergyBuckets.tsx` so the Revenue page's discharge-value chart
 * shares the exact same numbers.
 */
export function BessEnergyBrowser() {
  const topology = useTopology();
  const t = useT();
  // View prefs (PERS-09 / BESS-11): granularity + unit scope persist per
  // site in ems-ui-prefs; the URL never carries them (widget-card
  // semantics), so precedence is pref → default. Stored values are
  // re-validated on every read: an unknown gran falls back to daily;
  // scope ids that left the topology (site swap, stale pref) are dropped
  // and an empty result falls back to the full fleet.
  const [storedGran, setGran] = useUiPref<EnergyGran>("bessEnergy.gran", "d");
  const gran: EnergyGran = BAR_WIDTH_BY_GRAN[storedGran] ? storedGran : "d";
  const [storedScope, setScope] = useUiPref<string[]>("bessEnergy.scope", []);
  const scope = useMemo(() => {
    const validIds = new Set(topology.bess.map((b) => b.id));
    const filtered = Array.isArray(storedScope)
      ? storedScope.filter((id) => validIds.has(id))
      : [];
    return filtered.length ? filtered : topology.bess.map((b) => b.id);
  }, [storedScope, topology.bess]);

  const { aggregated, totalCap, isLoading, isError } = useBessEnergyBuckets(gran, scope);

  // KPIs across the visible window — derived from `aggregated` so the
  // capacity-cap filter automatically flows into the totals.
  const totals = useMemo(() => {
    const charged = aggregated.reduce((s, x) => s + x.charged_kWh, 0);
    const discharged = aggregated.reduce((s, x) => s + x.discharged_kWh, 0);
    const throughput = charged + discharged;
    const cycles = cyclesFromThroughput(throughput, totalCap);
    return { charged, discharged, net: charged - discharged, throughput, cycles };
  }, [aggregated, totalCap]);

  const empty = !isLoading && aggregated.every((s) => s.charged_kWh === 0 && s.discharged_kWh === 0);

  // Trailing bucket still filling (today / this week …) → fade its bar and
  // explain it, so a half-height bar never reads as a collapse (Never-lie).
  // A 1-min tick (not a frozen Date.now()) so the marker clears promptly once
  // the bucket completes — not only on the next 30 s data refetch.
  const now = useNow(60_000);
  const partialTs = partialBucketTs(aggregated, gran, now);
  const inProgress = t("analyze.energy.inProgress");

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{t("analyze.energy.cardTitle")}</CardTitle>
          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-text-muted">
            <span>{t("analyze.energy.desc")}</span>
            <HelpHint title={t("analyze.energy.helpTitle")}>
              {t("analyze.energy.helpBody")} {t("analyze.energy.helpTech")}
            </HelpHint>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GranChips value={gran} onChange={setGran} />
        </div>
      </CardHeader>

      <ScopeChips
        topology={topology.bess}
        scope={scope}
        onChange={setScope}
      />

      <div className="p-3">
        {isLoading ? (
          <ChartSkeleton height={340} />
        ) : isError ? (
          <EmptyState
            title={t("analyze.energy.errTitle")}
            detail={t("analyze.energy.errDetail")}
            action={
              <div className="text-[10px] text-text-faint">
                {t("analyze.energy.errTech", { gran })}
              </div>
            }
            height={340}
          />
        ) : empty ? (
          <EmptyState
            title={t("analyze.revenue.emptyCoarseTitle")}
            detail={t("analyze.energy.emptyDetail")}
            action={
              <div className="text-[10px] text-text-faint">
                {t("analyze.energy.emptyTech", { keys: `EP_${gran}/EN_${gran}` })}
              </div>
            }
            height={340}
          />
        ) : (
          <>
            <DivergingBarChart samples={aggregated} gran={gran} partialTs={partialTs} partialLabel={inProgress} />
            {partialTs != null && NOTE_GRANS.has(gran) && (
              <p className="mt-2 text-[10px] text-text-faint">{t("analyze.energy.partialNote")}</p>
            )}
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 border-t border-edge bg-surface-2/30 p-4 md:grid-cols-4">
        <Kpi icon={BatteryCharging} tone="charge" label={t("analyze.energy.kpiCharged")}
             value={formatKWh(totals.charged, 0)} unit="kWh" />
        <Kpi icon={BatteryLow}      tone="discharge" label={t("analyze.energy.kpiDischarged")}
             value={formatKWh(totals.discharged, 0)} unit="kWh" />
        <Kpi icon={Layers} label={t("analyze.energy.kpiNet")}
             value={`${totals.net >= 0 ? "+" : "−"}${formatKWh(Math.abs(totals.net), 0)}`}
             unit="kWh"
             sub={totals.net >= 0 ? t("analyze.energy.netGained") : t("analyze.energy.netDelivered")} />
        <Kpi icon={Battery} label={t("analyze.energy.kpiThroughput")}
             value={formatKWh(totals.throughput, 0)} unit="kWh"
             sub={t(totals.cycles === 1 ? "analyze.energy.cyclesOne" : "analyze.energy.cycles", { n: totals.cycles.toFixed(2) })} />
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Diverging bar chart                                                         */
/* -------------------------------------------------------------------------- */

function DivergingBarChart({ samples, gran, partialTs, partialLabel }: {
  samples: BessEnergySample[];
  gran: EnergyGran;
  /** ts of the trailing in-progress bucket (faded + tooltip-flagged), or null. */
  partialTs: number | null;
  /** Translated "in progress" suffix for the partial bucket's tooltip header. */
  partialLabel: string;
}) {
  // Repaint on theme flips (DSGN-07): tokens are read inside the memo, so
  // bumping the version re-resolves every colour for the new theme.
  const themeVersion = useThemeVersion();
  const option = useMemo(() => {
    const base = baseOption();
    const t = chartTokens();
    // Charge above zero (positive), discharge below zero (negative) so
    // the operator reads net flow visually as "where the bar sits". The
    // trailing in-progress bucket (if any) is emitted in object form with a
    // lowered opacity — ECharts merges this over the series itemStyle, so the
    // bar keeps its colour/radius and only fades.
    const fade = (ts: number, value: number) =>
      ts === partialTs ? { value: [ts, value], itemStyle: { opacity: PARTIAL_OPACITY } } : [ts, value];
    const chargeData = samples.map((s) => fade(s.ts, s.charged_kWh));
    const dischargeData = samples.map((s) => fade(s.ts, -s.discharged_kWh));
    // Bar width per granularity. ECharts time-axis bars auto-size from
    // the minimum interval between data points; if one week is
    // 7 days but another is 6 days (e.g. an AE2 batch boundary), the
    // computed width collapses to the smaller gap and every bar renders
    // pencil-thin. Pinning barWidth in px keeps the chart readable at
    // every granularity.
    const barW = BAR_WIDTH_BY_GRAN[gran];
    return {
      ...base,
      grid: { ...base.grid, top: 24, bottom: 40, left: 56, right: 16 },
      xAxis: {
        ...(base.xAxis as object),
        type: "time",
        axisLabel: {
          color: t.textMuted, fontSize: 11,
          formatter: bucketAxisLabel(gran),
        },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          color: t.textMuted, fontSize: 11,
          formatter: (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${formatKWh(Math.abs(v), 0)} kWh`,
        },
        splitLine: { lineStyle: { color: t.grid, type: "dashed" } },
      },
      legend: { ...(base.legend as object), data: ["Charged", "Discharged"] },
      tooltip: {
        ...tooltipBase(),
        formatter: (params: Array<{ axisValue: number; data: BarDatum; seriesName: string; color: string }>) => {
          if (!params?.length) return "";
          const ts = params[0].axisValue;
          // The partial bucket's datum is object-form ({ value: [ts, v], … }),
          // so `data[1]` would be undefined and silently read as 0 kWh — a lie
          // about the in-progress bar. Read the y through barDatumValue, which
          // unwraps both the tuple and object forms.
          const charged = barDatumValue(params.find((p) => p.seriesName === "Charged")?.data);
          const discharged = -barDatumValue(params.find((p) => p.seriesName === "Discharged")?.data);
          const net = charged - discharged;
          const header = ts === partialTs
            ? `${bucketTooltipLabel(ts, gran)} · ${partialLabel}`
            : bucketTooltipLabel(ts, gran);
          return tooltipHtml({
            header,
            rows: [
              { color: t.charge, label: "Charged", value: `+${formatKWh(charged, 1)}`, unit: "kWh" },
              { color: t.discharge, label: "Discharged", value: `−${formatKWh(discharged, 1)}`, unit: "kWh" },
              {
                color: net >= 0 ? t.charge : t.discharge,
                label: "Net",
                value: `${net >= 0 ? "+" : "−"}${formatKWh(Math.abs(net), 1)}`,
                unit: "kWh",
              },
            ],
          });
        },
      },
      series: [
        {
          name: "Charged",
          type: "bar",
          stack: "ec",
          data: chargeData,
          itemStyle: barItemStyle({ sign: "positive", color: t.charge }),
          barWidth: barW,
          barMaxWidth: barW,
          emphasis: { focus: "series" },
          // Faint Sat–Sun backdrop on DAILY only (empty/undefined otherwise),
          // a NEUTRAL structural tint (--ems-rule-strong, lighter than the grid
          // lines) so it never collides with the physics/price/alarm palette.
          // `silent` keeps it from intercepting hover/click. Read inside the
          // memo so a theme flip re-resolves the colour.
          markArea: gran === "d" ? {
            silent: true,
            itemStyle: { color: readVar("--ems-rule-strong") },
            data: weekendMarkAreaData(samples, gran),
          } : undefined,
        },
        {
          name: "Discharged",
          type: "bar",
          stack: "ec",
          data: dischargeData,
          itemStyle: barItemStyle({ sign: "negative", color: t.discharge }),
          barWidth: barW,
          barMaxWidth: barW,
          emphasis: { focus: "series" },
        },
      ],
    } as object;
  }, [samples, gran, partialTs, partialLabel, themeVersion]);
  return <ReactECharts option={option} notMerge style={{ height: 340 }} lazyUpdate />;
}
